import cls from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { WorkspaceBanner } from 'src/components/base/WorkspaceBanner';
import { VerticalSplitter } from 'src/components/base/Splitter';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import { useFetchGroups, useUpdateGroup } from 'src/model/groups';
import { useFetchTraps, useUpdateTrap } from 'src/model/traps';
import { useCurrentWorkspace } from 'src/model/workspaces';
import { useEffectState } from 'src/utils/hooks';
import { hasPermission, PERMISSION_GROUP_EDIT, PERMISSION_TRAP_EDIT, PERMISSION_TRAP_EDIT_MAP } from 'src/api/permissions';
import { useSystemStore } from 'src/stores';

import { isValidPosition } from 'src/utils/helpers';

import { SidePanel } from './SidePanel';
import { MapPanel } from './MapPanel';

import classnames from './Map.module.scss';

export const Map = observer(() => {
  const [center, setCenter] = useState();
  const [minimized, setMinimized] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('groups');

  const [selectedItem, setSelectedItem] = useState();
  const location = useLocation();
  const history = useHistory();
  const { workspace: { workspace_id } } = useCurrentWorkspace();
  const { workspaces, workspace: currentWorkspaceDetails, user } = useSystemStore();
  const buildingMapWarningModal = useModalDialog();
  const buildingMapWarningText = __('The trap is currently placed on the building map. If you place it on Google Maps it will be removed from the building map.');

  const previousSearchRef = useRef(location.search);
  const userUnselectedRef = useRef(false);

  const groupsResponse = useFetchGroups({ workspace_id, pageIndex: 0, pageSize: 1000, sortBy: { id: undefined, desc: undefined }, filter: { key: 'type', value: 'user' } });
  const trapsResponse = useFetchTraps({ workspace_id, pageSize: 1000 });

  const groupsData = groupsResponse.data ?? { items: [] };
  const [updateGroupAction] = useUpdateGroup(groupsResponse.mutate);
  const [updateTrapAction] = useUpdateTrap(trapsResponse.mutate);

  const dataItems = groupsData.items ?? [];
  const trapDataItems = trapsResponse.data?.items ?? [];

  const resolveWorkspacePermissions = useCallback((targetWorkspaceId) => {
    if (Array.isArray(workspaces)) {
      const match = workspaces.find(({ workspace_id: id }) => id === targetWorkspaceId);
      if (match) {
        return match.permissions;
      }
    }

    if (currentWorkspaceDetails?.workspace_id === targetWorkspaceId) {
      return currentWorkspaceDetails.permissions;
    }

    return user?.permissions;
  }, [workspaces, currentWorkspaceDetails, user]);

  const canEditGroup = useCallback((targetWorkspaceId) => (
    hasPermission(resolveWorkspacePermissions(targetWorkspaceId), PERMISSION_GROUP_EDIT)
  ), [resolveWorkspacePermissions]);

  const canEditTrap = useCallback((targetWorkspaceId) => {
    const permissions = resolveWorkspacePermissions(targetWorkspaceId);
    return hasPermission(permissions, PERMISSION_TRAP_EDIT) || hasPermission(permissions, PERMISSION_TRAP_EDIT_MAP);
  }, [resolveWorkspacePermissions]);

  const canEditItem = useCallback((item) => {
    if (!item) { return false; }
    if (item.type === 'group') {
      return canEditGroup(item.workspaceId);
    }
    if (item.type === 'trap') {
      return canEditTrap(item.workspaceId);
    }
    return false;
  }, [canEditGroup, canEditTrap]);

  const [groupItems, setGroupItems] = useEffectState(() => dataItems.map((item, index) => {
    const { group_id, group_name, latitude, longitude, workspace_id: itemWorkspaceId } = item;
    const position = {
      lat: typeof latitude === 'number' ? latitude : null,
      lng: typeof longitude === 'number' ? longitude : null,
    };

    const workspaceId = Number.isFinite(Number(itemWorkspaceId)) ? Number(itemWorkspaceId) : itemWorkspaceId ?? null;

    return {
      type: 'group',
      index,
      group_id,
      group_name,
      markerKey: `group-${group_id}`,
      markerPriority: 'normal',
      position,
      changed: { ...position },
      workspaceId,
    };
  }), [dataItems]);

  const [trapItems, setTrapItems] = useEffectState(() => trapDataItems.map(({ trap_id, trap_name, event_priority, is_caught, event_name, serial_no, latitude, longitude, map_id, map_group_id, workspace_id: itemWorkspaceId }, index) => {
    const lat = typeof latitude === 'number' ? latitude : null;
    const lng = typeof longitude === 'number' ? longitude : null;
    const position = { lat, lng };
    const normalizedMapId = Number.isFinite(Number(map_id)) ? Number(map_id) : null;
    const normalizedMapGroupId = Number.isFinite(Number(map_group_id)) ? Number(map_group_id) : null;
    const workspaceId = Number.isFinite(Number(itemWorkspaceId)) ? Number(itemWorkspaceId) : itemWorkspaceId ?? null;

    return {
      type: 'trap',
      index,
      trap_id,
      trap_name,
      serial_no,
      event_priority,
      is_caught,
      event_name,
      position,
      changed: { ...position },
      buildingMapId: normalizedMapId,
      buildingMapGroupId: normalizedMapGroupId,
      workspaceId,
    }
  }), [trapDataItems]);

  const availableGroupIds = useMemo(() => {
    return new Set(groupItems.map(item => Number(item.group_id)));
  }, [groupItems]);

  const visibleGroupItems = useMemo(() => {
    return groupItems.filter(item => {
      // Check if group is assigned to Google map (has valid coordinates)
      const hasGoogleCoordinates = typeof item.position?.lat === 'number' && !Number.isNaN(item.position.lat) &&
                                    typeof item.position?.lng === 'number' && !Number.isNaN(item.position.lng);

      // If group is assigned to Google map, always show it
      if (hasGoogleCoordinates) {
        return true;
      }

      // If group is not assigned to Google map, only show if user can edit group location
      return canEditGroup(item.workspaceId);
    });
  }, [groupItems, canEditGroup]);

  const visibleTrapItems = useMemo(() => {
    return trapItems.filter(item => {
      // Check if trap is assigned to Google map (has valid coordinates)
      const hasGoogleCoordinates = typeof item.position?.lat === 'number' && !Number.isNaN(item.position.lat) &&
                                    typeof item.position?.lng === 'number' && !Number.isNaN(item.position.lng);
      // Check if trap is assigned to building map
      const hasBuilingMap = Number.isFinite(item.buildingMapId);
      const isAssignedToAnyMap = hasGoogleCoordinates || hasBuilingMap;

      // If trap is on a building map of an inaccessible group AND user cannot edit trap location, hide it
      if (hasBuilingMap && Number.isFinite(item.buildingMapGroupId)) {
        const groupIsAvailable = availableGroupIds.has(item.buildingMapGroupId);
        const userCanEditTrap = canEditTrap(item.workspaceId);
        if (!groupIsAvailable && !userCanEditTrap) {
          return false;
        }
      }

      // If trap is assigned to any map, always show it (if group/permission check passed)
      if (isAssignedToAnyMap) {
        return true;
      }

      // If trap is not assigned to any map, only show if user can edit trap location
      return canEditTrap(item.workspaceId);
    });
  }, [trapItems, canEditTrap, availableGroupIds]);

  const mapItems = useMemo(() => [...visibleGroupItems, ...visibleTrapItems], [visibleGroupItems, visibleTrapItems]);

  const canToggleEditMode = useMemo(() => {
    const canEditGroups = groupItems.some(item => canEditGroup(item.workspaceId));
    const canEditTraps = trapItems.some(item => canEditTrap(item.workspaceId));
    return canEditGroups || canEditTraps;
  }, [groupItems, trapItems, canEditGroup, canEditTrap]);

  useEffect(() => {
    if (!canToggleEditMode && editMode) {
      setEditMode(false);
    }
  }, [canToggleEditMode, editMode]);

  const handleEditModeChange = useCallback((value) => {
    if (!canToggleEditMode) { return; }
    setEditMode(value);
  }, [canToggleEditMode]);

  // Check if both groups and traps have finished loading from API
  const dataLoaded = useMemo(() => {
    return groupsResponse.isValidating === false && 
           groupsResponse.data !== undefined &&
           trapsResponse.isValidating === false && 
           trapsResponse.data !== undefined;
  }, [groupsResponse.isValidating, groupsResponse.data, trapsResponse.isValidating, trapsResponse.data]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trapIdParam = params.get('trap_id');
    const trapId = Number(trapIdParam);

    if (!trapIdParam || !Number.isFinite(trapId)) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    if (!trapItems.length) {
      return;
    }

    const match = trapItems.find(item => Number(item.trap_id) === trapId);
    if (!match) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    const selectedTrapId = Number(selectedItem?.trap_id);
    if (selectedTrapId === trapId) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    // Don't auto-select if user manually unselected
    if (userUnselectedRef.current) {
      previousSearchRef.current = location.search;
      return;
    }

    const searchChanged = previousSearchRef.current !== location.search;
    if (searchChanged || !selectedItem) {
      if (activeTab !== 'traps') {
        setActiveTab('traps');
      }
      setSelectedItem(match);
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
    }
  }, [location.search, trapItems, activeTab, selectedItem]);

  function handleCancel() {
    setGroupItems(prevItems => prevItems.map(item => ({
      ...item,
      changed: { ...item.position }
    })));
    setTrapItems(prevItems => prevItems.map(item => ({
      ...item,
      changed: { ...item.position }
    })));
    setEditMode(false);
    setSelectedItem(null);
  }

  async function handleSaveChanges() {
  const groupUpdates = [];
    const trapUpdates = [];

    groupItems.forEach((item) => {
  if (!canEditGroup(item.workspaceId)) { return; }
      const wasValid = isValidPosition(item.position);
      const isValid = isValidPosition(item.changed);

      if (isValid && (!wasValid || item.changed.lat !== item.position.lat || item.changed.lng !== item.position.lng)) {
        groupUpdates.push(updateGroupAction({ group_id: item.group_id, latitude: item.changed.lat, longitude: item.changed.lng }));
      } else if (wasValid && !isValid) {
        groupUpdates.push(updateGroupAction({ group_id: item.group_id, latitude: '', longitude: '' }));
      }
    });

    trapItems.forEach((item) => {
  if (!canEditTrap(item.workspaceId)) { return; }
      const wasValid = isValidPosition(item.position);
      const isValid = isValidPosition(item.changed);

      if (isValid && (!wasValid || item.changed.lat !== item.position.lat || item.changed.lng !== item.position.lng)) {
        trapUpdates.push(updateTrapAction({ trap_id: item.trap_id, latitude: item.changed.lat, longitude: item.changed.lng }));
      } else if (wasValid && !isValid) {
        trapUpdates.push(updateTrapAction({ trap_id: item.trap_id, latitude: '', longitude: '' }));
      }
    });

    await Promise.all([...groupUpdates, ...trapUpdates]);

    if (groupUpdates.length > 0) {
      setGroupItems(prevItems => prevItems.map(item => {
        const newPosition = isValidPosition(item.changed) ? { ...item.changed } : { lat: null, lng: null };
        return { ...item, position: newPosition, changed: newPosition };
      }));
    }

    if (trapUpdates.length > 0) {
      setTrapItems(prevItems => prevItems.map(item => {
        const newPosition = isValidPosition(item.changed) ? { ...item.changed } : { lat: null, lng: null };
        return { ...item, position: newPosition, changed: newPosition };
      }));
    }

    setEditMode(false);
    setSelectedItem(null);

    await groupsResponse.mutate();
    await trapsResponse.mutate();
  }

  const persistItemPosition = useCallback(async (item) => {
    if (item.type === 'group') {
      const isValid = isValidPosition(item.changed);
      await updateGroupAction({ group_id: item.group_id, latitude: isValid ? item.changed.lat : '', longitude: isValid ? item.changed.lng : '' });
      const newPosition = isValid ? { ...item.changed } : { lat: null, lng: null };
      setGroupItems(prevItems => prevItems.map(current => (
        current.group_id === item.group_id
          ? { ...current, position: newPosition, changed: newPosition }
          : current
      )));
    } else if (item.type === 'trap') {
      const isValid = isValidPosition(item.changed);
      await updateTrapAction({ trap_id: item.trap_id, latitude: isValid ? item.changed.lat : '', longitude: isValid ? item.changed.lng : '' });
      const newPosition = isValid ? { ...item.changed } : { lat: null, lng: null };
      setTrapItems(prevItems => prevItems.map(current => (
        current.trap_id === item.trap_id
          ? { ...current, position: newPosition, changed: newPosition }
          : current
      )));
    }
  }, [setGroupItems, setTrapItems, updateGroupAction, updateTrapAction]);

  const handleRemoveFromMap = useCallback(async (item) => {
    if (!canEditItem(item)) { return; }

    if (item.type === 'group') {
      setGroupItems(prevItems => prevItems.map(current => (
        current.group_id === item.group_id
          ? { ...current, changed: { lat: null, lng: null } }
          : current
      )));
    }

    if (item.type === 'trap') {
      setTrapItems(prevItems => prevItems.map(current => (
        current.trap_id === item.trap_id
          ? { ...current, changed: { lat: null, lng: null } }
          : current
      )));
    }

    setSelectedItem(current => {
      if (!current) { return current; }
      if (item.type === 'group' && current.group_id === item.group_id) { return null; }
      if (item.type === 'trap' && current.trap_id === item.trap_id) { return null; }
      return current;
    });

    // Persist with cleared coordinates to avoid sending previous values
    const cleared = { ...item, changed: { lat: null, lng: null } };

    await persistItemPosition(cleared);
  }, [canEditItem, setGroupItems, setTrapItems, setSelectedItem, persistItemPosition]);

  async function handleDropItem(dropped) {
    if (!center || !canEditItem(dropped)) { return; }

    const updatedPosition = { ...center };
    let nextSelected = null;

    if (dropped.type === 'group') {
      setGroupItems(prevItems => prevItems.map(item => {
        if (item.index === dropped.index) {
          nextSelected = { ...item, changed: updatedPosition };
          return nextSelected;
        }
        return item;
      }));
    } else if (dropped.type === 'trap') {
      if (Number.isFinite(dropped.buildingMapId)) {
        const shouldContinue = await buildingMapWarningModal.show({ text: buildingMapWarningText, variant: 'primary', actionTitle: __('Proceed') });
        if (!shouldContinue) {
          return;
        }
      }
      setTrapItems(prevItems => prevItems.map(item => {
        if (item.trap_id === dropped.trap_id) {
          nextSelected = {
            ...item,
            changed: updatedPosition,
            buildingMapId: null,
            buildingMapGroupId: null
          };
          return nextSelected;
        }
        return item;
      }));
    }

    if (nextSelected) {
      setSelectedItem(nextSelected);
      await persistItemPosition(nextSelected);
    }
  }

  const handleItemPositionChange = useCallback(async (item) => {
    if (!canEditItem(item)) { return; }
    await persistItemPosition(item);
  }, [persistItemPosition, canEditItem]);

  const handleSelectItem = useCallback((item) => {
    if (item === null) {
      // Clear trap_id from URL when manually unselecting
      const params = new URLSearchParams(location.search);
      if (params.has('trap_id')) {
        params.delete('trap_id');
        const newSearch = params.toString();
        history.replace({ ...location, search: newSearch ? `?${newSearch}` : '' });
        userUnselectedRef.current = true;
      }
    }
    if (item?.type === 'trap') {
      setActiveTab('traps');
    } else if (item?.type === 'group') {
      setActiveTab('groups');
    }
    setSelectedItem(item);
  }, [history, location]);

  return (
    <>
      <div>
        <WorkspaceBanner />

        <div className={cls('d-flex flex-row flex-grow-1', classnames.root)}>

          <div className={cls(classnames.page, { 'd-none': minimized })}>
            <SidePanel groups={visibleGroupItems} traps={visibleTrapItems} activeTab={activeTab} onTabChange={setActiveTab}
              selectedItem={selectedItem} onSelectItem={handleSelectItem}
              editMode={editMode} onEditMode={handleEditModeChange} onDropItem={handleDropItem}
              onCancel={handleCancel} onSubmit={handleSaveChanges}
              canEditItem={canEditItem} canToggleEditMode={canToggleEditMode}
              availableGroupIds={availableGroupIds}
            />
          </div>

          <VerticalSplitter onClick={() => setMinimized(!minimized)} />

          <MapPanel items={mapItems} editMode={editMode} onCenterChanged={setCenter}
            selectedItem={selectedItem} onSelectItem={handleSelectItem} onRemoveFromMap={handleRemoveFromMap}
            onItemPositionChange={handleItemPositionChange} canEditItem={canEditItem}
            dataLoaded={dataLoaded}
          />

        </div>

      </div>

      <ConfirmModalDialog {...buildingMapWarningModal.register()} />
    </>
  )
})
